// src/components/commandSearch/commandSearchService.js
//
// Command Search Service
// ======================
//
// PURPOSE
// -------
// Encapsulates frontend calls to the backend-powered global Command Search
// endpoint.
//
// This service is intentionally kept outside Navbar.
// Navbar is only one possible entry point for Command Search.
// The search dialog and provider own the UI flow, while this service owns the
// HTTP call.
//
// BACKEND ENDPOINT
// ----------------
// Expected backend endpoint:
//
//   GET /search?q=...
//
// If your backend mounts API routes differently, adjust only this constant:
//
//   const GLOBAL_SEARCH_ENDPOINT = "/search";
//
// Example alternatives:
//   "/api/search"
//   "/command-search"
//   "/global-search"
//
// AUTH / SESSION MODEL
// --------------------
// This endpoint is protected and should be guarded on backend by:
//
//   validateSessionAndToken
//
// and optionally:
//
//   roleMiddleware(["superuser", "reception", "user"])
//
// Important:
// The backend must enforce result visibility per domain.
// The frontend must never receive records the user is not allowed to see.
//
// WHY withCredentials IS EXPLICIT HERE
// ------------------------------------
// Even if apiClient has withCredentials configured globally, this service keeps
// withCredentials: true explicitly for consistency with the rest of hb_visits
// services.
//
// This is important because Command Search is an authenticated endpoint and may
// rely on refresh/session cookies in addition to access-token headers handled by
// apiClient.
//
// EXPECTED BACKEND RESPONSE SHAPE
// -------------------------------
// Preferred:
//
//   {
//     results: [
//       {
//         id: "product:123",
//         type: "product",
//         group: "Products",
//         title: "Olej kokosowy",
//         subtitle: "SKU: ... · Barcode: ...",
//         path: "/catalog/products/123",
//         score: 0.91
//       }
//     ]
//   }
//
// Also supported defensively:
//
//   [
//     { ...result }
//   ]
//

import apiClient from "../../utils/apiClient";
import { normalizeBackendSearchResult } from "./commandSearchUtils";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

const GLOBAL_SEARCH_ENDPOINT = "/search";

function getErrorMessage(err) {
  const apiMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail;

  return apiMsg || err?.message || "Request failed";
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);

  e.status = err?.response?.status;
  e.data = err?.response?.data;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[CommandSearchService] ${context}`, {
      status: e.status,
      message,
      data: e.data,
    });
  }

  throw e;
}

/**
 * Fetch global Command Search results from the backend.
 *
 * Parameters:
 * - query:
 *     user-entered search text
 *
 * - options.signal:
 *     AbortController signal used by the dialog to cancel stale requests while
 *     the user keeps typing.
 *
 * Returns:
 * - normalized array of command-search result objects
 *
 * Notes:
 * - empty query returns [] without calling backend
 * - malformed backend payload returns [] defensively
 * - backend errors are wrapped in project-style API errors
 */
export async function fetchGlobalCommandSearchResults(query, options = {}) {
  const q = String(query || "").trim();

  if (!q) {
    return [];
  }

  try {
    const response = await apiClient.get(GLOBAL_SEARCH_ENDPOINT, {
      withCredentials: true,
      params: { q },
      signal: options.signal,
    });

    const payload = response?.data;

    const rows = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.results)
      ? payload.results
      : [];

    return rows.map(normalizeBackendSearchResult).filter(Boolean);
  } catch (err) {
    throwApiError("fetchGlobalCommandSearchResults", err);
  }
}

export default {
  fetchGlobalCommandSearchResults,
};